import { useMemo } from 'react';
import { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import { useAppSelector } from './reduxHook';
import { Option } from '../store/referenceData.slice';
import { isTelematics } from '../utils';

/**
 * Hook to generate annual mileage dropdown options for both telematics and non-telematics scenarios
 * @param currentAnnualMileage - The user's current annual mileage
 * @returns An object containing annual mileage options and a function to get business mileage options
 */
const useGetMileageOptions = (currentAnnualMileage?: number | string) => {
	const policy = useAppSelector((state) => state.fullPolicy);
	const isTelematicsCustomer = import.meta.env.VITE_AUTH_REALM === 'MyPolicy' || isTelematics(policy.product);

	const lists = useAppSelector((redux) => redux.referenceData);
	const nonTelematicsAnnualMileage = lists?.millageIncrementOptions;
	// Generate mileage options based on whether it's telematics or not
	const annualMileageOptions = useMemo<IOptions[]>(() => {
		// If it's telematics, generate options from 1000 to 30000 in increments of 1000
		if (isTelematicsCustomer) {
			const options: IOptions[] = [];
			for (let mileage = 1000; mileage <= 30000; mileage += 1000) {
				options.push({
					value: mileage,
					label: mileage.toLocaleString(),
				});
			}
			return options;
		}
		// If it's not telematics, use the options from the redux store
		else if (nonTelematicsAnnualMileage && nonTelematicsAnnualMileage.length > 0) {
			return nonTelematicsAnnualMileage.map((mileage: Option) => ({
				value: mileage.value as string | number,
				label: mileage.label as string | number,
			}));
		}
		// Fallback to empty array if nonTelematicsAnnualMileage is not available
		return [];
	}, [isTelematics, nonTelematicsAnnualMileage]);

	/**
	 * Function to filter business mileage options based on annual mileage
	 * @param annualMileage - The annual mileage to filter against
	 * @returns Filtered business mileage options
	 */
	const getBusinessMileageOptions = (annualMileage: string | number): IOptions[] => {
		const mileageValue = typeof annualMileage === 'string' ? parseInt(annualMileage, 10) : annualMileage;

		return annualMileageOptions.filter((option) => (option.value as number) <= mileageValue);
	};

	// If currentAnnualMileage is provided, pre-filter the business options
	const businessMileageOptions = useMemo(() => {
		if (!currentAnnualMileage) return [];

		return getBusinessMileageOptions(currentAnnualMileage);
	}, [currentAnnualMileage, annualMileageOptions]);

	return {
		annualMileageOptions,
		businessMileageOptions,
		getBusinessMileageOptions,
	};
};

export default useGetMileageOptions;
